<template>
  <div v-if="show" class="miniflow-login-overlay" @click.self="onBackdropClick">
    <div class="login-container">
      <!-- Brand -->
      <div class="logo-section">
        <h1 class="app-title">Nxtedge.ai</h1>
        <p class="app-subtitle">Workflow Automation Platform</p>
      </div>

      <!-- Login Form -->
      <div class="form-section">
        <div class="login-card">
          <h2>Sign In</h2>
          <p class="login-description">Enter your credentials to access your workflows</p>
          
          <form @submit.prevent="handleLogin" class="login-form">
            <!-- Email Field -->
            <div class="form-group">
              <label for="email">Email</label>
              <input
                id="email"
                v-model="email"
                type="email"
                placeholder="your@email.com"
                required
                autocomplete="email"
                :disabled="isLoading"
              />
            </div>

            <!-- Password Field -->
            <div class="form-group">
              <label for="password">Password</label>
              <div class="password-input-wrapper">
                <input
                  id="password"
                  v-model="password"
                  :type="showPassword ? 'text' : 'password'"
                  placeholder="Enter your password"
                  required
                  autocomplete="current-password"
                  :disabled="isLoading"
                />
                <button
                  type="button"
                  class="toggle-password"
                  @click="showPassword = !showPassword"
                  :aria-label="showPassword ? 'Hide password' : 'Show password'"
                >
                  <span class="password-icon">{{ showPassword ? '👁️' : '👁️‍🗨️' }}</span>
                </button>
              </div>
            </div>

            <!-- Error Message -->
            <div v-if="loginError" class="error-message">
              <span class="error-icon">⚠️</span>
              {{ loginError }}
            </div>

            <!-- Loading State -->
            <button
              type="submit"
              class="login-button"
              :disabled="isLoading"
            >
              <span v-if="isLoading" class="loading-spinner"></span>
              <span>{{ isLoading ? 'Signing in...' : 'Sign In' }}</span>
            </button>
          </form>

          <!-- OIDC Provider Buttons (if configured) -->
          <div v-if="hasOidcProviders" class="oidc-providers">
            <div class="divider">
              <span>or</span>
            </div>
            <button class="oidc-button" @click="loginWithOidc">
              <span class="oidc-icon">🔐</span>
              Sign in with Provider
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useLocalAuthStore, type TokenResult } from '@/stores/localAuthStore'

defineProps<{
  show: boolean
}>()

const emit = defineEmits<{
  'login-success': [result: TokenResult]
  'login-failure': [error: string]
}>()

const authStore = useLocalAuthStore()

// Form state
const email = ref('')
const password = ref('')
const showPassword = ref(false)
const isLoading = ref(false)
const loginError = ref<string | null>(null)

const hasOidcProviders = ref(false) // Will be set from OIDC settings

function onBackdropClick() {
  // Optionally close on backdrop click
}

async function handleLogin() {
  isLoading.value = true
  loginError.value = null

  try {
    const result: TokenResult = await authStore.login(email.value, password.value)
    
    if (result.success) {
      loginError.value = null
      emit('login-success', result)
    } else {
      loginError.value = result.error || 'Login failed'
      emit('login-failure', result.error || 'Login failed')
    }
  } catch (err) {
    loginError.value = err instanceof Error ? err.message : 'An unexpected error occurred'
    emit('login-failure', loginError.value)
  } finally {
    isLoading.value = false
  }
}

async function loginWithOidc() {
  // Redirect to OIDC provider or open provider login
  // This would be configured in the OIDC settings
  loginError.value = 'OIDC provider not configured'
}
</script>

<style scoped>
.miniflow-login-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  backdrop-filter: blur(10px);
}

.login-container {
  display: flex;
  flex-direction: row;
  background: #141416;
  border: 1px solid #26262a;
  border-radius: 16px;
  overflow: hidden;
  max-width: 900px;
  width: 90%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.6);
}

.logo-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  background: linear-gradient(135deg, #1c1c20 0%, #0c0c0e 100%);
}

.app-title {
  color: #f4f4f5;
  font-size: 2rem;
  font-weight: 700;
  margin: 0;
  letter-spacing: -0.5px;
}

.app-subtitle {
  color: #8a8a92;
  font-size: 1rem;
  margin-top: 8px;
}

.form-section {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
}

.login-card {
  width: 100%;
  max-width: 360px;
}

.login-card h2 {
  color: #ffffff;
  font-size: 1.75rem;
  margin: 0 0 8px 0;
}

.login-description {
  color: #a0a0b0;
  font-size: 0.9rem;
  margin: 0 0 24px 0;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  color: #e0e0e0;
  font-size: 0.875rem;
  margin-bottom: 6px;
  font-weight: 500;
}

.form-group input {
  width: 100%;
  padding: 12px 16px;
  background: #1b1b1f;
  border: 1px solid #33333a;
  border-radius: 8px;
  color: #ffffff;
  font-size: 1rem;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.form-group input:focus {
  outline: none;
  border-color: #6b7280;
  box-shadow: 0 0 0 3px rgba(107, 114, 128, 0.25);
}

.form-group input:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.password-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.password-input-wrapper input {
  padding-right: 48px;
}

.toggle-password {
  position: absolute;
  right: 8px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  font-size: 1rem;
}

.toggle-password:hover {
  opacity: 0.8;
}

.error-message {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: rgba(233, 69, 96, 0.15);
  border: 1px solid rgba(233, 69, 96, 0.3);
  border-radius: 8px;
  color: #ff6b8a;
  font-size: 0.875rem;
  margin-bottom: 16px;
}

.login-button {
  width: 100%;
  padding: 14px 24px;
  background: linear-gradient(135deg, #3f3f46 0%, #27272a 100%);
  color: #ffffff;
  border: 1px solid #52525b;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s, background 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.login-button:hover:not(:disabled) {
  transform: translateY(-1px);
  background: linear-gradient(135deg, #52525b 0%, #3f3f46 100%);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.5);
}

.login-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.loading-spinner {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.oidc-providers {
  margin-top: 24px;
}

.divider {
  display: flex;
  align-items: center;
  text-align: center;
  margin: 16px 0;
  color: #a0a0b0;
  font-size: 0.875rem;
}

.divider::before,
.divider::after {
  content: '';
  flex: 1;
  border-bottom: 1px solid #33333a;
}

.divider span {
  padding: 0 12px;
}

.oidc-button {
  width: 100%;
  padding: 12px 16px;
  background: #1b1b1f;
  color: #e0e0e0;
  border: 1px solid #33333a;
  border-radius: 8px;
  font-size: 0.9rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: background 0.2s, border-color 0.2s;
}

.oidc-button:hover {
  background: #26262a;
  border-color: #52525b;
}

@media (max-width: 768px) {
  .login-container {
    flex-direction: column;
  }

  .logo-section {
    padding: 24px;
  }

  .app-title {
    font-size: 1.5rem;
  }

  .form-section {
    padding: 24px;
  }
}
</style>
